import React, { useEffect } from "react";

import { ErrorMessage, Textfield, PhoneWithIsdField } from "widgets";
import Loader from "Shared/Loader/Loader";
import { useFetchPostalCodeDetails } from "store/backend/assets";

import styles from "./AddressForm.module.scss";
import { LEARN_VERTICAL_ID } from "constants/config";
import {
  getAddressConfigByCountryCode,
  POSTAL_CODE_FORMATS,
  resolvePostalCodeFormat,
} from "constants/addressConfig";

const generateFieldProps = (addressType, field) => ({
  id: `${addressType}-${field}`,
  name: `${addressType}[${field}]`,
});

const FieldLabel = ({ htmlFor, children, required }) => (
  <label className={styles.fieldLabel} htmlFor={htmlFor} title={children}>
    {children}
    {required ? <span className={styles.required}>*</span> : null}
  </label>
);

const FieldError = ({ children }) => (
  <div className={styles.fieldError}>
    <ErrorMessage>{children}</ErrorMessage>
  </div>
);

const AddressFields = (props) => {
  const {
    address,
    handleChangeFields,
    disabled,
    errors,
    handleBlur,
    addressType,
    validateForm,
    selectedVerticalId,
    countryName,
    isdCode,
    countryCode,
    gatewayTenantId,
  } = props;

  const config = getAddressConfigByCountryCode(countryCode);
  const usesPostalLookup = Boolean(config.usesPostalLookup);
  const postalCodeFormatKey = resolvePostalCodeFormat(countryCode, addressType);
  const postalCodeMaxLength =
    POSTAL_CODE_FORMATS[postalCodeFormatKey]?.maxLength;
  const nameLabelPrefix =
    addressType === "shipping"
      ? "Shipping"
      : selectedVerticalId === LEARN_VERTICAL_ID
        ? "Billing"
        : "Student";
  const emailLabel =
    addressType === "shipping"
      ? "Shipping Email"
      : selectedVerticalId === LEARN_VERTICAL_ID
        ? "Billing Email"
        : "Parent Email";

  const {
    isFetching: isPostalCodeDetailsFetching,
    isPending: isPostalCodeDetailsLoading,
    data: postalCodeDetails,
    isError,
    error,
  } = useFetchPostalCodeDetails(
    usesPostalLookup ? address?.pincode : undefined,
    gatewayTenantId,
  );

  const {
    district: lookupDistrict = { id: "", name: "" },
    state: lookupState = { id: "", name: "" },
  } = postalCodeDetails || {};

  useEffect(() => {
    if (!usesPostalLookup || !postalCodeDetails || !address) return;

    const { postal_code, district, state } = postalCodeDetails;

    if (postal_code.id !== address.pincodeId) {
      handleChangeFields("pincodeId", postal_code.id);
      handleChangeFields("districtId", district.id);
      handleChangeFields("district", district.name);
      handleChangeFields("state", state.name);
      handleChangeFields("stateId", state.id);
      setTimeout(() => {
        validateForm();
      }, 0);
    }
  }, [
    usesPostalLookup,
    postalCodeDetails,
    address,
    address?.pincodeId,
    validateForm,
    handleChangeFields,
  ]);

  const fieldErrors = errors?.[`${addressType}Address`] || {};
  const isParentNameRequired = addressType === "billing";
  const isLookupInProgress =
    isPostalCodeDetailsLoading && isPostalCodeDetailsFetching;

  return (
    <div className={styles.formGrid}>
      <div className={styles.textField}>
        <FieldLabel
          htmlFor={`${addressType}-recipientName`}
          required
        >{`${nameLabelPrefix} Name`}</FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "recipientName")}
          value={address?.recipientName || ""}
          handleOnChange={(event) =>
            handleChangeFields("recipientName", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.recipientName && (
          <FieldError>{fieldErrors.recipientName}</FieldError>
        )}
      </div>
      <div className={styles.textField}>
        <FieldLabel
          htmlFor={`${addressType}-parentName`}
          required={isParentNameRequired}
        >
          Parent Name
        </FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "parentName")}
          value={address?.parentName || ""}
          handleOnChange={(event) =>
            handleChangeFields("parentName", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.parentName && (
          <FieldError>{fieldErrors.parentName}</FieldError>
        )}
      </div>
      <div className={`${styles.textField} ${styles.phoneWithIsd}`}>
        <FieldLabel
          htmlFor={`${addressType}-recipientPhoneNumber`}
          required
        >{`${nameLabelPrefix} Phone Number`}</FieldLabel>
        <PhoneWithIsdField
          id={`${addressType}-recipientPhoneNumber`}
          label=""
          isdCode={isdCode}
          phone={address?.recipientPhoneNumber || ""}
          onPhoneChange={(nextPhone) =>
            handleChangeFields("recipientPhoneNumber", nextPhone)
          }
          error={fieldErrors.recipientPhoneNumber}
          disabled={disabled}
          lockIsd
          className={styles.billingPhoneField}
        />
      </div>
      <div className={styles.textField}>
        <FieldLabel htmlFor={`${addressType}-recipientEmail`} required>
          {emailLabel}
        </FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "recipientEmail")}
          value={address?.recipientEmail || ""}
          handleOnChange={(event) =>
            handleChangeFields("recipientEmail", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.recipientEmail && (
          <FieldError>{fieldErrors.recipientEmail}</FieldError>
        )}
      </div>
      <div className={styles.textField}>
        <FieldLabel htmlFor={`${addressType}-addressLine1`} required>
          Address Line 1
        </FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "addressLine1")}
          value={address?.addressLine1 || ""}
          handleOnChange={(event) =>
            handleChangeFields("addressLine1", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.addressLine1 && (
          <FieldError>{fieldErrors.addressLine1}</FieldError>
        )}
      </div>
      <div className={styles.textField}>
        <FieldLabel htmlFor={`${addressType}-addressLine2`}>
          Address Line 2
        </FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "addressLine2")}
          value={address?.addressLine2 || ""}
          handleOnChange={(event) =>
            handleChangeFields("addressLine2", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.addressLine2 && (
          <FieldError>{fieldErrors.addressLine2}</FieldError>
        )}
      </div>
      <div className={styles.textField}>
        <FieldLabel htmlFor={`${addressType}-street`}>Street</FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "street")}
          value={address?.street || ""}
          handleOnChange={(event) =>
            handleChangeFields("street", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.street && <FieldError>{fieldErrors.street}</FieldError>}
      </div>
      <div className={styles.textField}>
        <FieldLabel htmlFor={`${addressType}-landmark`}>Landmark</FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "landmark")}
          value={address?.landmark || ""}
          handleOnChange={(event) =>
            handleChangeFields("landmark", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.landmark && <FieldError>{fieldErrors.landmark}</FieldError>}
      </div>
      <div className={styles.textField}>
        <FieldLabel htmlFor={`${addressType}-city`} required>
          City
        </FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "city")}
          value={address?.city || ""}
          handleOnChange={(event) =>
            handleChangeFields("city", event.target.value)
          }
          disabled={disabled}
          onBlur={handleBlur}
        />
        {fieldErrors.city && <FieldError>{fieldErrors.city}</FieldError>}
      </div>
      {config.showPostalCode && (
        <div className={styles.textField}>
          <FieldLabel htmlFor={`${addressType}-pincode`} required>
            {usesPostalLookup ? "Pincode" : "Postal Code"}
          </FieldLabel>
          <Textfield
            {...generateFieldProps(addressType, "pincode")}
            value={address?.pincode || ""}
            handleOnChange={(event) => {
              handleChangeFields("pincode", event.target.value);
            }}
            disabled={disabled}
            inputProps={
              postalCodeMaxLength ? { maxLength: postalCodeMaxLength } : undefined
            }
            onBlur={handleBlur}
          />
          {fieldErrors.pincode && (
            <FieldError>{fieldErrors.pincode}</FieldError>
          )}
          {isError ? (
            <FieldError>
              {error.message || "Error fetching pincode details"}
            </FieldError>
          ) : null}
        </div>
      )}
      {config.showDistrict && (
        <div className={styles.textField}>
          <FieldLabel htmlFor={`${addressType}-district`} required>
            {config.requireDistrictName ? "Area/District" : "District"}
          </FieldLabel>
          {config.districtReadOnly && isLookupInProgress ? (
            <div className={styles.loaderSlot}>
              <Loader />
            </div>
          ) : (
            <Textfield
              {...generateFieldProps(addressType, "district")}
              value={
                config.districtReadOnly
                  ? lookupDistrict.name
                  : address?.district || ""
              }
              handleOnChange={
                config.districtReadOnly
                  ? undefined
                  : (event) =>
                      handleChangeFields("district", event.target.value)
              }
              disabled={disabled || config.districtReadOnly}
              onBlur={handleBlur}
            />
          )}
          {fieldErrors.district && (
            <FieldError>{fieldErrors.district}</FieldError>
          )}
        </div>
      )}
      {config.showState && (
        <div className={styles.textField}>
          <FieldLabel htmlFor={`${addressType}-state`} required>
            {config.isUsState ? "State (e.g. NY, TX)" : "State"}
          </FieldLabel>
          {config.stateReadOnly && isLookupInProgress ? (
            <div className={styles.loaderSlot}>
              <Loader />
            </div>
          ) : (
            <Textfield
              {...generateFieldProps(addressType, "state")}
              value={
                config.stateReadOnly ? lookupState.name : address?.state || ""
              }
              handleOnChange={
                config.stateReadOnly
                  ? undefined
                  : (event) => handleChangeFields("state", event.target.value)
              }
              disabled={disabled || config.stateReadOnly}
              onBlur={handleBlur}
            />
          )}
          {fieldErrors.state && <FieldError>{fieldErrors.state}</FieldError>}
        </div>
      )}
      <div className={styles.textField}>
        <FieldLabel htmlFor={`${addressType}-country`} required>
          Country
        </FieldLabel>
        <Textfield
          {...generateFieldProps(addressType, "country")}
          value={countryName}
          disabled={true}
          onBlur={handleBlur}
        />
      </div>
    </div>
  );
};

export default AddressFields;
